import store from "@/store";
import {captions_el} from "@/locales/captions";
import {alertModal} from "@/util/ComponentUtils";
import {SORT_TYPE} from "@/util/EnumUtil";
import {isEqual} from "lodash";

export const isEmpty = (value) => {
    return value === undefined || value === null || Number.isNaN(value) || (typeof value === 'object' && Object.keys(value).length === 0 || (typeof value === 'string'
        && value.trim().length === 0));
};

export const merge = (original, updates) => {
    for (let prop in updates) {
        if (!Object.hasOwn(updates, prop)) {
            continue;
        }
        original[prop] = updates[prop];
    }
    return original;
};

export const isInt = (value) => {
    return !isNaN(value) &&
        isFinite(value) &&
        Math.floor(value) == value;
};

export const isNumeric = (str) => {
    if (typeof str != "string") return false // we only process strings!
    return !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
        !isNaN(parseFloat(str)) // ...and ensure strings of whitespace fail
};

export const decimalFormatter = (item) => {
    if (isNumeric(item)) {
        item = formatAmount(item)
    }
    return item
};

export const upperCaseFormatter = (item) => {
    if (!isEmpty(item)) {
        return item.toUpperCase()
    } else {
        return item
    }
};

export const maskedNumber = (item) => {
    if (item.length <= 4) {
        const lastTwoDigits = item.slice(-2)
        const maskedDigits = '*'.repeat(item.length - 2);
        return maskedDigits + lastTwoDigits;
    }
    const lastTwoDigits = item.slice(-2);
    const firstTwoDigits = item.slice(0, 2);
    const maskedDigits = '*'.repeat(item.length - 4);
    return firstTwoDigits + maskedDigits + lastTwoDigits;
}
export const afmFormatter = (item) => {
    if (!isEmpty(item) && !isEmpty(item)) {
        return (item.toString()).padStart(9, '0')
    } else {
        return item
    }
};

export const formatAmount = (amount) => {
    amount = unFormatAmount(amount);
    let numOfDecimals = 2;
    let decimalsRegex = new RegExp("\\.(\\d{" + numOfDecimals + "})$");
    return parseFloat(amount).toFixed(numOfDecimals).replace(/\d(?=(\d{3})+\.)/g, '$&,')
        .replace(/,/g, ".")
        .replace(decimalsRegex, ",$1");
};

export const unFormatAmount = (amount) => {
    if (amount === '' || (amount + "").trim().match(/[^\d,.]/g)) {
        return null;
    } else {
        amount = (amount + "").trim();
        //if it ends with a , followed by one or more digits then this is decimal separator
        //or if it ends with one dot followed by three digits
        //or if it has many dots then they are used as thousand separators
        let numOfDots = amount.match(/\./g);
        if (amount.match(/,\d{1,}$/) || (amount.match(/\.\d{3}$/)) || (numOfDots !== null && numOfDots.length > 1)) {
            amount = amount.replace(/\./g, "").replace(/,/g, ".");
        }

        return amount;

    }
};

export const mergeJsonObjects = (target, source) => {
    for (let key1 in source) {
        for (let key in target) {
            if (Object.prototype.hasOwnProperty.call(target, key1)) {
                if (key1 === key)
                    target[key] = source[key1]
            }
        }
    }
    return target
}

export const sortCompare = (aRow, bRow, key, sortDesc, formatter, compareOptions, compareLocale) => {
    const a = aRow[key] // or use Lodash `_.get()`
    const b = bRow[key]

    if (
        (typeof a === 'number' && typeof b === 'number') ||
        (a instanceof Date && b instanceof Date)
    ) {
        // If both compared fields are native numbers or both are native dates
        return a < b ? -1 : a > b ? 1 : 0
    } else {
        // Otherwise stringify the field data and use String.prototype.localeCompare
        return toString(a).localeCompare(toString(b), compareLocale, compareOptions)
    }
}

// Helper function to stringify the values of an Object
export const toString = (value) => {
    if (value === null || typeof value === 'undefined') {
        return ''
    } else if (value instanceof Object) {
        return Object.keys(value)
            .sort()
            .map(key => toString(value[key]))
            .join(' ')
    } else {
        return String(value)
    }
}

export const getCaption = (label, ...labels) => {
    let caption
    if (!isEmpty(store.getters['caption/getCaptions'])) {
        let savedLabel = store.getters['caption/getCaptions'].filter(row => {
            if (row.code == label) {
                return row
            }
        })[0]
        caption = !isEmpty(savedLabel) ? savedLabel.caption : captions_el[label]
        for (let i = 0; i < labels.length; i++) {
            if (!isEmpty(labels[i])) {
                caption = caption.toString().replaceAll('%@' + (i + 1), labels[i])
            }
        }
        return caption
    } else {
        caption = captions_el[label]
        for (let i = 0; i < labels.length; i++) {
            if (!isEmpty(labels[i])) {
                caption = caption.toString().replaceAll('%@' + (i + 1), labels[i])
            }
        }
    }
    return caption
}

export const isPropValuesEqual = (subject, target, propNames) => {
    return propNames.every(propName => {
        if (propName.includes('.')) {
            let propArray = propName.split('.')
            if (propArray.length === 2) {
                return subject[propArray[0]][propArray[1]] === target[propArray[0]][propArray[1]];
            }
            if (propArray.length === 3) {
                return subject[propArray[0]][propArray[1]][propArray[2]] === target[propArray[0]][propArray[1]][propArray[2]];
            }

        } else {
            return subject[propName] === target[propName];
        }
    })
}

export const getUniqueItemsByProperties = (items, propNames) => {
    const propNamesArray = Array.from(propNames);
    return items.filter((item, index, array) =>
        index === array.findIndex(foundItem => isPropValuesEqual(foundItem, item, propNamesArray))
    );
};

/**
 * Shows errors msg with specific error data.
 * @param instance (this).
 * @param response response of endpoint.
 * @return alertModal
 */
export const showErrorDialog = (instance, response) => {
    if (response !== null && [400, 401, 404, 500].includes(response.status) && (response.data.error === 'avc' || response.data.error === 'avu')) {
        alertModal(instance, getCaption('ERROR_VALIDATION_SYZYGOS_AFM_EXIST', instance.$route.params.etos), {
            size: "md",
            buttonSize: 'md',
            headerClass: 'p-2 border-bottom-2 border-bottom  border-light',
            footerClass: 'p-2 border-top-2 border-top  border-light',
            okTitle: 'Αποδοχή',
            cancelVariant: 'danger',
            okVariant: 'success',
            contentClass: 'text-dark font-weight-bolder',
        }, 'msgBoxOk')
    } else if (response !== null && [400, 401, 404, 500].includes(response.status) && response.data.error === 'oic') {
        alertModal(instance, getCaption('ERROR_VALIDATION_SYZYGOS_AFM_OPEN_ORIA', ''), {
            size: "md",
            buttonSize: 'md',
            headerClass: 'p-2 border-bottom-2 border-bottom  border-light',
            footerClass: 'p-2 border-top-2 border-top  border-light',
            okTitle: 'Αποδοχή',
            cancelVariant: 'danger',
            okVariant: 'success',
            contentClass: 'text-dark font-weight-bolder',
        }, 'msgBoxOk')
    } else if (response !== null && [400, 401, 404, 500].includes(response.status) && response.data.error === 'vle') {
        alertModal(instance, getCaption('ERROR_VALIDATION_HM_LHXHS_SYZ_SXESIS', ''), {
            size: "md",
            buttonSize: 'md',
            headerClass: 'p-2 border-bottom-2 border-bottom  border-light',
            footerClass: 'p-2 border-top-2 border-top  border-light',
            okTitle: 'Αποδοχή',
            cancelVariant: 'danger',
            okVariant: 'success',
            contentClass: 'text-dark font-weight-bolder',
        }, 'msgBoxOk')
    } else if (response !== null && [400, 401, 404, 500].includes(response.status) && response.data.error === 'eve') {
        alertModal(instance, getCaption('ERROR_VALIDATION_YEAR_INVALID', ''), {
            size: "md",
            buttonSize: 'md',
            headerClass: 'p-2 border-bottom-2 border-bottom  border-light',
            footerClass: 'p-2 border-top-2 border-top  border-light',
            okTitle: 'Αποδοχή',
            cancelVariant: 'danger',
            okVariant: 'success',
            contentClass: 'text-dark font-weight-bolder',
        }, 'msgBoxOk')
    } else if (response !== null && [400, 401, 404, 500].includes(response.status) && response.data.error === 'ase') {
        alertModal(instance, getCaption('ERROR_VALIDATION_AFM_DUPLICATE', ''), {
            size: "md",
            buttonSize: 'md',
            headerClass: 'p-2 border-bottom-2 border-bottom  border-light',
            footerClass: 'p-2 border-top-2 border-top  border-light',
            okTitle: 'Αποδοχή',
            cancelVariant: 'danger',
            okVariant: 'success',
            contentClass: 'text-dark font-weight-bolder',
        }, 'msgBoxOk')
    } else if (response !== null && [400, 401, 404, 500].includes(response.status) && response.data.error === 'bdve') {
        alertModal(instance, getCaption('ERROR_VALIDATION_AFM_DUPLICATE', ''), {
            size: "md",
            buttonSize: 'md',
            headerClass: 'p-2 border-bottom-2 border-bottom  border-light',
            footerClass: 'p-2 border-top-2 border-top  border-light',
            okTitle: 'Αποδοχή',
            cancelVariant: 'danger',
            okVariant: 'success',
            contentClass: 'text-dark font-weight-bolder',
        }, 'msgBoxOk')
    } else if (response !== null && [400, 401, 404, 500].includes(response.status) && response.data.error === 'dfa') {
        alertModal(instance, getCaption('ERROR_VALIDATION_DIF_AFM', ''), {
            size: "md",
            buttonSize: 'md',
            headerClass: 'p-2 border-bottom-2 border-bottom  border-light',
            footerClass: 'p-2 border-top-2 border-top  border-light',
            okTitle: 'Αποδοχή',
            cancelVariant: 'danger',
            okVariant: 'success',
            contentClass: 'text-dark font-weight-bolder',
        }, 'msgBoxOk')
    } else {
        alertModal(instance, getCaption('Η αποθήκευση απέτυχε. Παρακαλώ προσπαθήστε ξανά. Αν το πρόβλημα δεν επιλυθεί παρακαλώ επικοινωνήστε με τον διαχειριστή της εφαρμογής.'), {
            size: "md",
            buttonSize: 'md',
            headerClass: 'p-2 border-bottom-2 border-bottom  border-light',
            footerClass: 'p-2 border-top-2 border-top  border-light',
            okTitle: 'Αποδοχή',
            cancelVariant: 'danger',
            okVariant: 'success',
            contentClass: 'text-dark font-weight-bolder',
        }, 'msgBoxOk')
    }
}
/**
 * Sorts a table items with specific table column.
 * @param items item for sorting
 * @param sortColumn property which sorting
 * @param sortType type os sorting
 * @returns {*}
 */
export const sort = (items, sortColumn, sortType) => {
    if (sortType === SORT_TYPE.DESC || sortType === null) {
        return items.sort((a, b) => parseFloat(b[sortColumn]) - parseFloat(a[sortColumn]));
    }
    if (sortType === SORT_TYPE.ASC) {
        return items.sort((a, b) => parseFloat(a[sortColumn]) - parseFloat(b[sortColumn]));
    }
}

export const checkIfExistSpecValue = (arrayValue, value) => {
    return arrayValue.includes(value);
}

/**
 * Checks if specific value exist inside table's row. Use it in validator.js file.
 */
export const checkIfExistsInTable = (array, model, value, propName) => {
    if (!isEmpty(array)) {
        return !array.some(child => {
            if (!isEqual(child, model)) {
                if (typeof propName === 'object' && Object.keys(propName).length === 2) {
                    return child[propName[0]][propName[1]] == value
                } else {
                    return child[propName] == value
                }
            }
        })
    }
    return true
};
/**
 * Reset the input Object with null values
 * @return Object
 */
export const resetObject = (model) => {
    Object.keys(model).forEach(key => {
        if (Array.isArray(model[key])) {
            model[key] = []
        } else {
            model[key] = null;
        }
    });
    return model
}